/**
 * Mirrors {@link @internal/plugin-catalog-backend-module-rithum-ai} kinds for the
 * frontend plugin only (ESLint forbids importing the catalog backend module here).
 */
import type { Entity } from '@backstage/catalog-model';

export const RITHUM_AI_API_VERSION = 'rithum.com/v1alpha1' as const;

export interface AgentEntitySpec {
  type: 'markdown' | 'custom-runtime';
  owner: string;
  lifecycle: 'experimental' | 'production' | 'deprecated';
  system?: string;
  model?: string;
  skills?: string[];
  mcps?: string[];
  consumesApis?: string[];
  platform?: 'claude-code' | 'claude-agent-sdk' | 'cursor' | 'copilot' | 'other';
  sourcePath?: string;
  framework?: 'strands' | 'langchain' | 'langgraph' | 'claude-agent-sdk' | 'custom';
  language?: 'python' | 'typescript' | 'go' | 'other';
  runtime?: 'container' | 'serverless-function' | 'lambda' | 'batch-job';
  trigger?: 'always-on' | 'scheduled' | 'event-driven' | 'on-demand';
  schedule?: string;
  eventSource?: string;
}

export type AgentEntityV1alpha1 = Entity & {
  apiVersion: typeof RITHUM_AI_API_VERSION;
  kind: 'Agent';
  spec: AgentEntitySpec;
};

export function isAgentEntityV1alpha1(
  entity: Entity,
): entity is AgentEntityV1alpha1 {
  return (
    entity.apiVersion === RITHUM_AI_API_VERSION && entity.kind === 'Agent'
  );
}

export interface SkillEntitySpec {
  type: 'markdown' | 'tool-bundle' | 'workflow';
  owner: string;
  lifecycle: 'experimental' | 'production' | 'deprecated';
  allowedTools?: string[];
  tools?: string[];
  knowledge?: string[];
  mcps?: string[];
  dependsOnSkills?: string[];
}

export type SkillEntityV1alpha1 = Entity & {
  apiVersion: typeof RITHUM_AI_API_VERSION;
  kind: 'Skill';
  spec: SkillEntitySpec;
};

export function isSkillEntityV1alpha1(
  entity: Entity,
): entity is SkillEntityV1alpha1 {
  return (
    entity.apiVersion === RITHUM_AI_API_VERSION && entity.kind === 'Skill'
  );
}

export interface MCPAuthPathway {
  type: 'none' | 'api-key' | 'bearer' | 'oauth2' | 'basic' | 'mtls' | 'custom';
  description?: string;
  scopes?: string[];
  tokenUrl?: string;
  audience?: string;
}

export interface MCPEntitySpec {
  protocol: 'http' | 'sse' | 'stdio' | 'websocket';
  owner: string;
  lifecycle: 'experimental' | 'production' | 'deprecated';
  system?: string;
  endpoint?: string;
  command?: string;
  args?: string[];
  auth?: MCPAuthPathway[];
  providesTools?: string[];
  providesResources?: string[];
  providesPrompts?: string[];
  dependsOn?: string[];
  consumesApis?: string[];
}

export type MCPEntityV1alpha1 = Entity & {
  apiVersion: typeof RITHUM_AI_API_VERSION;
  kind: 'MCP';
  spec: MCPEntitySpec;
};

export function isMCPEntityV1alpha1(
  entity: Entity,
): entity is MCPEntityV1alpha1 {
  return (
    entity.apiVersion === RITHUM_AI_API_VERSION && entity.kind === 'MCP'
  );
}
